import { Component, createEffect, createSignal, JSX, onMount, onCleanup, Show } from 'solid-js'
import Footer from '/web/shared/aifans/Footer'
import GenerateImageOptionButton from '/web/shared/aifans/GenerateImage/GenerateImageOptionButton'
import GenerateImageBodyOptions from '/web/shared/aifans/GenerateImage/GenerateImageBodyOptions'
import GenerateImageActionOptions from '/web/shared/aifans/GenerateImage/GenerateImageActionOptions'
import GenerateImageClothingOptions from '/web/shared/aifans/GenerateImage/GenerateImageClothingOptions'
import GenerateImageAccessoriesOptions from '/web/shared/aifans/GenerateImage/GenerateImageAccessoriesOptions'
import GenerateImageViewOptions from '/web/shared/aifans/GenerateImage/GenerateImageViewOptions'
import { useNavigate, useParams } from '@solidjs/router'
import { AppSchema } from '/common/types'
import { getAssetUrl } from '/web/shared/util'
import { characterStore, toastStore, userStore } from '/web/store'
import GenerateImageCharacterPicker from '/web/shared/aifans/GenerateImage/GenerateImageCharacterPicker'
import GenerateImageCharacterModal from '/web/shared/aifans/GenerateImage/GenerateImageCharacterModal'
import { imageApi } from '/web/store/data/image'
import HeaderContainer from '/web/shared/aifans/HeaderContainer'
import ChatLeftNavContainer from './Chat/ChatLeftNavContainer'
import Progress from './Progress'
import { Link, Meta, Title } from '@solidjs/meta'
import { getCanonicalUrl } from '/web/shared/aifans/lib/config-util'
import InappropriateContentModal from './InappropriateContentModal'

const SectionTitle: Component<{ children: JSX.Element }> = (props) => {
  return <p class="mb-3 text-md text-cosplay-gray-100">{props.children}</p>
}

const TabOption: Component<{ children: JSX.Element; selected?: boolean; onClick: () => void }> = (
  props
) => {
  return (
    <button
      onClick={props.onClick}
      class={`rounded-md px-4 py-1 text-md text-cosplay-gray-100 ${
        props.selected ? 'bg-[#404040]' : ''
      }`}
    >
      {props.children}
    </button>
  )
}

const NumberOption: Component<{
  children: JSX.Element
  selected?: boolean
  premium?: boolean
  onClick: () => void
}> = (props) => {
  return (
    <button
      onClick={props.onClick}
      class={`relative flex h-16 w-16 items-center justify-center rounded-md border text-2xl md:h-20  md:w-20 ${
        props.selected
          ? 'border-white bg-gradient-to-br from-cosplay-purple to-cosplay-blue-200'
          : 'border-cosplay-gray-100/50 bg-[#313131]'
      }`}
    >
      <span>{props.children}</span>
      <Show when={props.premium}>
        <div class="absolute right-0 top-0 h-[15px] rounded-bl-md rounded-tr-md bg-cosplay-purple px-1 text-xxs leading-[15px]">
          Premium
        </div>
      </Show>
    </button>
  )
}

const DEFAULT_PROMPT = 'at comic-con, posing on the convention floor with fans and booths'
const PROMPT_STORAGE_KEY = 'generate-image-prompt'

const GenerateImage: Component = () => {
  const params = useParams()
  const nav = useNavigate()

  // Restore prompt from localStorage or use default
  const getInitialPrompt = () => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(PROMPT_STORAGE_KEY)
      return saved || DEFAULT_PROMPT
    }
    return DEFAULT_PROMPT
  }

  const [prompt, setPrompt] = createSignal(getInitialPrompt())
  const [character, setCharacter] = createSignal<AppSchema.Character | undefined>(undefined)
  const [showCharacterPicker, setShowCharacterPicker] = createSignal<boolean>(false)
  const [characters, setCharacters] = createSignal<AppSchema.Character[]>([])
  const [activeTab, setActiveTab] = createSignal(0)
  const [numberOfImages, setNumberOfImages] = createSignal(1)
  const [image, setImage] = createSignal<string | undefined>(undefined)
  const [generating, setGenerating] = createSignal<boolean>(false)
  const [showInappropriateContentModal, setShowInappropriateContentModal] =
    createSignal<boolean>(false)
  const [inappropriateContent, setInappropriateContent] = createSignal<string | undefined>(
    undefined
  )

  // character store
  const chars = characterStore((s) => ({
    list: s.characters.list,
    map: s.characters.list.reduce<Record<string, AppSchema.Character>>(
      (prev, curr) => Object.assign(prev, { [curr._id]: curr }),
      {}
    ),
    loaded: s.characters.loaded,
  }))

  const updatePrompt = (value: string, replacePrompt: boolean) => {
    if (replacePrompt) {
      setPrompt(value)
      if (typeof window !== 'undefined') {
        localStorage.setItem(PROMPT_STORAGE_KEY, value)
      }
    } else {
      let p: string = prompt()
      const trimmedValue = value.trim()

      // Check if the value is already in the prompt (for deselection)
      if (p.includes(trimmedValue)) {
        // Remove the value from the prompt
        p = p.replace(`, ${trimmedValue}`, '')
        p = p.replace(`${trimmedValue}, `, '')
        p = p.replace(trimmedValue, '')

        // Clean up any double commas or trailing commas
        p = p.replace(/,,+/g, ',')
        p = p.replace(/^,\s*/, '') // Remove leading comma
        p = p.replace(/,\s*$/, '') // Remove trailing comma

        setPrompt(p)
        if (typeof window !== 'undefined') {
          localStorage.setItem(PROMPT_STORAGE_KEY, p)
        }
      } else {
        // Add the value to the prompt
        if (p && !p.endsWith(',')) {
          p = `${p}, `
        }
        const newPrompt = `${p}${trimmedValue}`
        setPrompt(newPrompt)
        if (typeof window !== 'undefined') {
          localStorage.setItem(PROMPT_STORAGE_KEY, newPrompt)
        }
      }
    }
  }

  const generateImage = async () => {
    const charId = character()?._id

    if (!charId) {
      setShowCharacterPicker(true)
      return
    }

    setGenerating(true)
    setImage(undefined)
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    })

    if (charId) {
      try {
        const res = await imageApi.generateFanImage(prompt(), 'summary', charId)

        if (res.error) {
          // Check if it's a token insufficiency error
          if (res.error.includes('Insufficient tokens')) {
            userStore.showNoTokensModal(true)
          } else if (res.error.includes('contains inappropriate content')) {
            setInappropriateContent(res.error)
            setShowInappropriateContentModal(true)
          } else {
            toastStore.error(`Failed to request image: ${res.error}`)
          }
        } else {
          const url = res?.result?.output

          const image = url ? getAssetUrl(url, true) : ''

          if (userStore().user?.admin && res?.result?.info) {
            console.log(JSON.stringify(JSON.parse(res?.result?.info), null, 2))
          }

          image && setImage(image)
        }
      } catch (error: any) {
        // Check if it's a token insufficiency error
        if (error.message.includes('Insufficient tokens')) {
          userStore.showNoTokensModal(true)
        } else if (error.message.includes('contains inappropriate content')) {
          setInappropriateContent(error.message)
          setShowInappropriateContentModal(true)
        } else {
          toastStore.error(`Failed to request image: ${error.message}`)
        }
      } finally {
        setGenerating(false)
      }
    }
  }

  createEffect(() => {
    if (!params.id) {
      return
    }

    const char = chars.list.find((c) => c._id === params.id)
    setCharacter(char)
    setCharacters(chars.list)
  })

  onMount(async () => {
    if (!chars.loaded) {
      await characterStore.getCharacters()
    }
    setCharacters(chars.list)
  })

  // Clear prompt from localStorage when component unmounts (user navigates away)
  onCleanup(() => {
    if (typeof window !== 'undefined') {
      localStorage.removeItem(PROMPT_STORAGE_KEY)
    }
  })

  return (
    <>
      <Title>CosplayFans - Generate Cosplay Images</Title>
      <Meta name="og:title" content="CosplayFans - Generate Cosplay Images" />
      <Meta
        name="description"
        content="Create stunning AI-generated cosplay images on CosplayFans.ai. Choose your style, character, and more with our powerful AI image generator exclusives."
      />
      <Meta
        name="og:description"
        content="Create stunning AI-generated cosplay images on CosplayFans.ai. Choose your style, character, and more with our powerful AI image generator exclusives."
      />
      <Meta
        name="og:image"
        content="https://cdn.cosplayfanscdn.com/assets/char-7abb4c7b-f0ff-4f56-aeca-9ed8ba655811.png"
      />
      <Link rel="canonical" href={getCanonicalUrl('/generate-image/')} />
      <div class="h-full w-full bg-black">
        <div class="h-full w-full">
          <div class="relative w-full">
            <HeaderContainer />
            <ChatLeftNavContainer />
            <div
              id="main"
              class="mb-16 flex flex-1 flex-col pt-14 md:mb-0 md:min-h-[calc(100vh-82px)] md:pt-0 lg:ml-[222px]"
            >
              <div class="flex flex-col md:mt-[82px]">
                <div class="mx-5 mt-4 flex pb-0 md:mt-3 md:px-4 md:pb-0 lg:px-[20px] xl:px-[40px] 2xl:px-[60px]">
                  <span class="font-clash text-[28px] font-bold md:text-[40px]">
                    Generate Image
                  </span>
                </div>
                <div class="grid gap-4 md:grid-cols-2 md:px-4 md:py-5 lg:px-[20px] xl:px-[40px] 2xl:px-[60px]">
                  <div class="flex-1">
                    <Show when={!image()}>
                      <div class="mx-5 flex h-[calc(100vh-440px)] flex-col items-center justify-center rounded-md bg-cosplay-gray-800/90 px-4 py-4 md:h-full md:px-8 md:py-8">
                        <Show when={!generating()}>
                          <img
                            src="/images/generate-image-icon-lg.png"
                            class="mt-1 h-[55px] w-[55px] md:h-[75px] md:w-[75px]"
                          />
                          <p class="mt-2 text-center font-clash text-lg font-bold md:text-xl">
                            Enter a prompt and click Generate
                          </p>
                        </Show>
                        <Show when={generating()}>
                          <Progress completion={false} />
                        </Show>
                      </div>
                    </Show>
                    <Show when={image()}>
                      <div class="relative mx-5 flex h-[calc(100vh-440px)] flex-col items-center justify-center overflow-hidden rounded-md bg-cosplay-gray-800/90 px-4 py-4 md:h-full md:shrink-0 md:grow-0 md:px-8 md:py-8">
                        {image() && (
                          <img
                            class="absolute h-full w-full object-cover"
                            src={getAssetUrl(image() || '', true)}
                          />
                        )}
                      </div>
                    </Show>
                  </div>
                  <div>
                    <div class="mx-5 mt-4 md:mx-0 md:mt-0">
                      <div class="flex flex-col rounded-md bg-cosplay-gray-800/90 px-4 pb-5 pt-2 md:py-4">
                        <SectionTitle>Choose character</SectionTitle>
                        <GenerateImageCharacterPicker
                          character={character()}
                          onPressChange={() => setShowCharacterPicker(true)}
                        />
                      </div>
                      <div class="mt-4 flex flex-col rounded-md bg-cosplay-gray-800/90 px-4 py-2 md:py-4">
                        <SectionTitle>Create prompt - What would you like to see?</SectionTitle>
                        <textarea
                          class="mb-5 h-24 rounded-md border border-cosplay-gray-100/50 bg-cosplay-gray-800/90 p-3 md:h-28"
                          value={prompt()}
                          disabled={true}
                          id="prompt-textarea"
                          name="prompt-textarea"
                        />
                        <div class="mb-5 flex flex-row flex-wrap gap-3">
                          <GenerateImageOptionButton
                            title="Comic-Con"
                            value="at comic-con, posing on the convention floor with fans and booths"
                            onClick={updatePrompt}
                            replacePrompt
                            isSelected={prompt().includes(
                              'at comic-con, posing on the convention floor with fans and booths'
                            )}
                          />
                          <GenerateImageOptionButton
                            title="Streetwear"
                            value="wearing streetwear fashion leaning against a graffiti wall"
                            onClick={updatePrompt}
                            replacePrompt
                            isSelected={prompt().includes(
                              'wearing streetwear fashion leaning against a graffiti wall'
                            )}
                          />
                          <GenerateImageOptionButton
                            title="Cinema Lights"
                            value="in a smoky alley lit by neon cinema lights"
                            onClick={updatePrompt}
                            replacePrompt
                            isSelected={prompt().includes(
                              'in a smoky alley lit by neon cinema lights'
                            )}
                          />
                          <GenerateImageOptionButton
                            title="Red Carpet"
                            value="on a red carpet with flashing cameras"
                            onClick={updatePrompt}
                            replacePrompt
                            isSelected={prompt().includes('on a red carpet with flashing cameras')}
                          />
                          <GenerateImageOptionButton
                            title="Ballroom"
                            value="in a ballroom with elegant lighting"
                            onClick={updatePrompt}
                            replacePrompt
                            isSelected={prompt().includes('in a ballroom with elegant lighting')}
                          />
                          <GenerateImageOptionButton
                            title="Fantasy Castle"
                            value="standing in front of a glowing fantasy castle at sunset"
                            onClick={updatePrompt}
                            replacePrompt
                            isSelected={prompt().includes(
                              'standing in front of a glowing fantasy castle at sunset'
                            )}
                          />
                          <GenerateImageOptionButton
                            title="Stealth Gear"
                            value="wearing black stealth gear under moonlight"
                            onClick={updatePrompt}
                            replacePrompt
                            isSelected={prompt().includes(
                              'wearing black stealth gear under moonlight'
                            )}
                          />
                          <GenerateImageOptionButton
                            title="Neon Lights"
                            value="posing at a neon-lit rooftop bar"
                            onClick={updatePrompt}
                            replacePrompt
                            isSelected={prompt().includes('posing at a neon-lit rooftop bar')}
                          />
                          <GenerateImageOptionButton
                            title="Swimwear"
                            value="wearing cosplay-inspired bikini at the beach"
                            onClick={updatePrompt}
                            replacePrompt
                            isSelected={prompt().includes(
                              'wearing cosplay-inspired bikini at the beach'
                            )}
                          />
                          <GenerateImageOptionButton
                            title="Desert"
                            value="exploring a desert temple at sunrise"
                            onClick={updatePrompt}
                            replacePrompt
                            isSelected={prompt().includes('exploring a desert temple at sunrise')}
                          />
                          <GenerateImageOptionButton
                            title="Carnival"
                            value="at a carnival midway with colorful balloons"
                            onClick={updatePrompt}
                            replacePrompt
                            isSelected={prompt().includes(
                              'at a carnival midway with colorful balloons'
                            )}
                          />
                          <GenerateImageOptionButton
                            title="Winter Scene"
                            value="in a snowy forest with falling snowflakes"
                            onClick={updatePrompt}
                            replacePrompt
                            isSelected={prompt().includes(
                              'in a snowy forest with falling snowflakes'
                            )}
                          />
                          <GenerateImageOptionButton
                            title="Magical Forest"
                            value="in a glowing magical forest with fireflies"
                            onClick={updatePrompt}
                            replacePrompt
                            isSelected={prompt().includes(
                              'in a glowing magical forest with fireflies'
                            )}
                          />
                          <GenerateImageOptionButton
                            title="Rain-Soaked"
                            value="in a rain-soaked city street at night with neon reflection"
                            onClick={updatePrompt}
                            replacePrompt
                            isSelected={prompt().includes(
                              'in a rain-soaked city street at night with neon reflection'
                            )}
                          />
                          <GenerateImageOptionButton
                            title="Home"
                            value="relaxing in bed with cozy blankets and soft lighting"
                            onClick={updatePrompt}
                            replacePrompt
                            isSelected={prompt().includes(
                              'relaxing in bed with cozy blankets and soft lighting'
                            )}
                          />
                          <GenerateImageOptionButton
                            title="Romantic"
                            value="on a balcony under a moonlit sky"
                            onClick={updatePrompt}
                            replacePrompt
                            isSelected={prompt().includes('on a balcony under a moonlit sky')}
                          />
                          <GenerateImageOptionButton
                            title="Throne Room"
                            value="wearing royal armor standing in the center of a throne room"
                            onClick={updatePrompt}
                            replacePrompt
                            isSelected={prompt().includes(
                              'wearing royal armor standing in the center of a throne room'
                            )}
                          />
                          <GenerateImageOptionButton
                            title="Bedroom"
                            value="lounging on a bed with soft blankets and warm lighting"
                            onClick={updatePrompt}
                            replacePrompt
                            isSelected={prompt().includes(
                              'lounging on a bed with soft blankets and warm lighting'
                            )}
                          />
                          <GenerateImageOptionButton
                            title="Rooftop"
                            value="on a rooftop with a glowing city skyline behind them"
                            onClick={updatePrompt}
                            replacePrompt
                            isSelected={prompt().includes(
                              'on a rooftop with a glowing city skyline behind them'
                            )}
                          />
                          <GenerateImageOptionButton
                            title="Mountains"
                            value="on a snowy mountain peak"
                            onClick={updatePrompt}
                            replacePrompt
                            isSelected={prompt().includes('on a snowy mountain peak')}
                          />
                        </div>
                        <SectionTitle>Customize your image</SectionTitle>
                        <div class="mb-5 flex flex-row flex-wrap gap-1 rounded-md bg-[#313131] p-1 2xl:justify-between">
                          <TabOption selected={activeTab() === 0} onClick={() => setActiveTab(0)}>
                            Action
                          </TabOption>
                          <TabOption selected={activeTab() === 1} onClick={() => setActiveTab(1)}>
                            Body
                          </TabOption>
                          <TabOption selected={activeTab() === 2} onClick={() => setActiveTab(2)}>
                            Clothing
                          </TabOption>
                          <TabOption selected={activeTab() === 3} onClick={() => setActiveTab(3)}>
                            Accessories
                          </TabOption>
                          <TabOption selected={activeTab() === 4} onClick={() => setActiveTab(4)}>
                            View
                          </TabOption>
                        </div>
                        <Show when={activeTab() === 0}>
                          <GenerateImageActionOptions
                            updatePrompt={updatePrompt}
                            currentPrompt={prompt()}
                          />
                        </Show>
                        <Show when={activeTab() === 1}>
                          <GenerateImageBodyOptions
                            updatePrompt={updatePrompt}
                            currentPrompt={prompt()}
                          />
                        </Show>
                        <Show when={activeTab() === 2}>
                          <GenerateImageClothingOptions
                            updatePrompt={updatePrompt}
                            currentPrompt={prompt()}
                          />
                        </Show>
                        <Show when={activeTab() === 3}>
                          <GenerateImageAccessoriesOptions
                            updatePrompt={updatePrompt}
                            currentPrompt={prompt()}
                          />
                        </Show>
                        <Show when={activeTab() === 4}>
                          <GenerateImageViewOptions
                            updatePrompt={updatePrompt}
                            currentPrompt={prompt()}
                          />
                        </Show>
                        <div class="mt-4 flex flex-col">
                          <Show when={false}>
                            <SectionTitle>Number of images</SectionTitle>
                            <div class="flex flex-row justify-between gap-3 md:justify-normal">
                              <NumberOption
                                selected={numberOfImages() === 1}
                                onClick={() => setNumberOfImages(1)}
                              >
                                1
                              </NumberOption>
                              <NumberOption
                                selected={numberOfImages() === 2}
                                onClick={() => setNumberOfImages(2)}
                                premium
                              >
                                2
                              </NumberOption>
                              <NumberOption
                                selected={numberOfImages() === 3}
                                onClick={() => setNumberOfImages(3)}
                                premium
                              >
                                3
                              </NumberOption>
                              <NumberOption
                                selected={numberOfImages() === 4}
                                onClick={() => setNumberOfImages(4)}
                                premium
                              >
                                4
                              </NumberOption>
                            </div>
                          </Show>
                          <div class="mt-4">
                            <Show when={generating()}>
                              <div
                                class={`flex h-9 w-full items-center justify-center gap-2 rounded-md border border-white bg-gradient-to-r from-[#B14DFF] to-[#4A72FF] pl-3 pr-[17px]  font-clash text-xl font-semibold `}
                                role="status"
                              >
                                <svg
                                  aria-hidden="true"
                                  class="inline h-5 w-5 animate-spin fill-purple-600 text-gray-200"
                                  viewBox="0 0 100 101"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                                    fill="currentColor"
                                  />
                                  <path
                                    d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                                    fill="currentFill"
                                  />
                                </svg>
                                <span class="sr-only">Loading...</span>
                              </div>
                            </Show>
                            <Show when={!generating()}>
                              <button
                                onClick={() => generateImage()}
                                class={`flex h-9 w-full items-center justify-center gap-2 rounded-md border border-white bg-gradient-to-r from-[#B14DFF] to-[#4A72FF] pl-3 pr-[17px]  font-clash text-xl font-semibold `}
                              >
                                <img src={'/images/generate-button-icon.png'} class="w-5" />
                                <span class="text-sm leading-4">GENERATE</span>
                                <div class="flex flex-row items-center justify-center gap-2 rounded-md bg-cosplay-purple px-2 py-1">
                                  <span class="text-sm leading-4">2</span>
                                  <img src={'/images/coins-stack.png'} class="h-5 w-5" />
                                </div>
                              </button>
                            </Show>
                            <p class="mt-4 text-sm leading-3">
                              Generated Image will be saved to Gallery
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <Footer />
              </div>
            </div>
          </div>
        </div>
      </div>

      <GenerateImageCharacterModal
        show={showCharacterPicker()}
        hide={() => setShowCharacterPicker(false)}
        characters={characters()}
        selectedCharacter={character()}
        onSelect={(c) => {
          setCharacter(c)
          setShowCharacterPicker(false)
          nav(`/generate-image/${c._id}`)
        }}
      />

      <InappropriateContentModal
        show={showInappropriateContentModal()}
        onClose={() => setShowInappropriateContentModal(false)}
        content={inappropriateContent()}
      />
    </>
  )
}

export default GenerateImage
